// App-level MRU imports
import { BottomBanner } from '@chiroup/components/BottomBanner';
import * as Sentry from '@sentry/react';
import { AppMRUProvider } from './contexts/appMru.context';
// import { Hub } from 'aws-amplify/utils';
import { MILLISECONDS } from '@/libs/core/src/lib/constants/time';
import { getCurrentUser } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { StopListenerCallback } from '@aws-amplify/core/dist/esm/Hub/types';
import { useNativeTheme } from '@chiroup/hooks/useNativeTheme';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Loading from './components/common/Loading';
import LoadingPage from './components/common/LoadingPage';
import ThirdPartyScripts from './components/common/ThirdPartyScripts';
import Toasts from './components/common/toasts/Toasts';
import Container from './components/layout/Container';
import SwitchLocations from './components/layout/SwitchLocations';
import PayJsManager from './components/payments/PayJsManager';
import { ManagedActionItemsProvider } from './contexts/managedActionItems.context';
import { ThemeContext } from './contexts/theme.context';
import { ToastProvider } from './contexts/toast.context';
import useLocalStorage, { LSType } from './hooks/useLocalStorage';
// Route-level lazy modules
const AppMRUDrawer = lazy(() => import('../../../libs/components/src/lib/AppMRUDrawer'));
const MarketingAddOns = lazy(() => import('./components/marketing-add-ons/MarketingAddOns'));
const OAuthConfirm = lazy(() => import('./components/oauth/OAuthConfirm'));
const OAuthInitiate = lazy(() => import('./components/oauth/OAuthInitiate'));
const ConsolidatedTransactionModal = lazy(
  () => import('./components/patients/detail/billing/transactions/ConsolidatedTransactionModal')
);
const ScheduleWrapper = lazy(() => import('./components/schedule/ScheduleWrapper'));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const payJS: any;

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// const forceLogoutTime = Number(import.meta.env.VITE_INACTIVITY_LOGOUT_TIME || 120);

const Home = lazy(() => import('./components/home/Home'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const Billing = lazy(() => import('./components/billing/BillingWrapper'));
const Reporting = lazy(() => import('./components/reporting/ReportingContainer'));
const Patients = lazy(() => import('./components/patients/Patients'));
const ClinicalSkills = lazy(() => import('./components/clinical-skills/ClinicalSkills'));
const PracticeResources = lazy(() => import('./components/practice-resources/PracticeResources'));
const Settings = lazy(() => import('./components/settings/Settings'));
const ReportContainer = lazy(() => import('./components/report/ReportContainer'));
const Admin = lazy(() => import('./components/admin/Admin'));
const LoggedOut = lazy(() => import('./components/logged-out/LoggedOut'));
const Research = lazy(() => import('./components/research/Research'));
const UpvotySSOLogin = lazy(() => import('./pages/UpvotySSOLogin'));
const FeatureRequests = lazy(() => import('./pages/FeatureRequests'));
const MRUExamplePage = lazy(() => import('./pages/MRUExamplePage'));

// const WhatsNew = lazy(() => import('./components/whats-new/WhatsNew'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: MILLISECONDS.inMinutes(5),
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const [authState, setAuthState] = useState<string>();
  const [user, setUser] = useState<any>();
  const nativeTheme = useNativeTheme();
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const { setItem, getItem, removeItem } = useLocalStorage();
  const [appLoading, setAppLoading] = useState<boolean>(false);

  const shouldRender = useMemo(() => {
    return ['local', 'dev', 'staging'].includes(import.meta.env.VITE_STAGE);
  }, []);

  // pay.js script loading handled by PayJsManager inside Router

  const toggleScheme = () => {
    const themeToUse = theme === 'light' ? 'dark' : 'light';
    setTheme(themeToUse);

    if (themeToUse === nativeTheme) {
      removeItem(LSType.user, 'theme');
    } else {
      setItem(LSType.user, 'theme', themeToUse);
    }
  };

  useEffect(() => {
    let isDestroyed = false;
    let stopListener: StopListenerCallback;

    const doIt = async () => {
      const currentUser = await getCurrentUser().catch(err => {
        console.error({ err });
      });

      if (currentUser && !isDestroyed) {
        setAuthState('authenticated');
        setUser(currentUser);
      }

      stopListener = Hub.listen('auth', data => {
        if (!isDestroyed) {
          if (data.payload.event === 'tokenRefresh_failure') {
            setAuthState('signedOut');
          } else if (data.payload.event === 'signedOut') {
            setAuthState('signedOut');
          } else if (data.payload.event === 'signedIn') {
            setAuthState('authenticated');
            setUser(data.payload.data);
          }
        }
      });
    };

    doIt();

    return () => {
      isDestroyed = true;
      stopListener?.();
    };
  }, []);

  useEffect(() => {
    const savedTheme = getItem(LSType.user, 'theme');
    setTheme(savedTheme || nativeTheme);
  }, [nativeTheme, getItem]);

  // Note: Location switcher keyboard shortcut (Ctrl+Shift+L) is now handled directly in SwitchLocations.tsx

  useEffect(() => {
    document.body.classList.add('dark:bg-dark-gray-900');
    document.body.classList.add('print:bg-white');
    document.body.classList.add('print:dark:bg-white');
    const method = theme === 'dark' ? 'add' : 'remove';
    document.documentElement.classList[method]('dark');
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppMRUProvider>
        <ManagedActionItemsProvider>
          <ToastProvider>
            <ThemeContext.Provider
              value={{
                theme,
              }}
            >
              <>
                <BrowserRouter>
                  <PayJsManager />
                  <ThirdPartyScripts shouldLoad={true} />
                  {appLoading ? (
                    <LoadingPage />
                  ) : authState === 'authenticated' && user ? (
                    <Container>
                      <Suspense fallback={<Loading />}>
                        {/* App-level MRU drawer placed at root level but inside AppMRUProvider */}
                        {authState === 'authenticated' && user && shouldRender && (
                          <AppMRUDrawer title="Recent CPT Codes" />
                        )}
                        <SentryRoutes>
                          <Route path="/" element={<Home />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/billing/*" element={<Billing />} />
                          <Route path="/reporting/*" element={<Reporting />} />
                          <Route path="/patients/*" element={<Patients />} />
                          <Route path="/schedule/*" element={<ScheduleWrapper />} />
                          <Route path="/research/*" element={<Research />} />
                          <Route path="/report/*" element={<ReportContainer />} />
                          {shouldRender ? (
                            <Route path="/mru-example" element={<MRUExamplePage />} />
                          ) : null}
                          <Route path="/clinical-skills/*" element={<ClinicalSkills />} />
                          <Route path="/practice-resources/*" element={<PracticeResources />} />
                          <Route
                            path="/settings/*"
                            element={<Settings theme={theme} toggleScheme={toggleScheme} />}
                          />
                          <Route path="/admin/*" element={<Admin />} />
                          {/* <Route path="/news">
                        <WhatsNew />
                      </Route> */}
                          {import.meta.env.VITE_STAGE !== 'sandbox' && (
                            <Route path="/marketing-add-ons/*" element={<MarketingAddOns />} />
                          )}
                          <Route
                            path="consolidated/transaction/*"
                            element={<ConsolidatedTransactionModal mode={'route'} />}
                          />
                          <Route path="/oauth/confirm" element={<OAuthConfirm />} />
                          <Route path="/oauth/initiate" element={<OAuthInitiate />} />
                          <Route path="/sso/upvoty" element={<UpvotySSOLogin />} />
                          <Route path="/feature-requests" element={<FeatureRequests />} />
                        </SentryRoutes>
                        {/* <Footer /> */}
                      </Suspense>
                    </Container>
                  ) : (
                    <Suspense fallback={<Loading />}>
                      <div className="h-full flex flex-col">
                        <div style={{ minHeight: 'calc(100vh - 100px)' }}>
                          <LoggedOut
                            authState={authState}
                            setAppLoading={setAppLoading}
                            theme={theme}
                          />
                        </div>
                        {/* <LoggedOutFooter /> */}
                      </div>
                    </Suspense>
                  )}
                </BrowserRouter>
                {/* <InactivityModal
              open={inactivityModalOpen}
              setOpen={setInactivityModalOpen}
              loggedOut={authState === 'signedOut'}
              setIsIdle={setIsIdle}
            /> */}
                <Toasts />
                <SwitchLocations />
              </>
            </ThemeContext.Provider>
          </ToastProvider>
        </ManagedActionItemsProvider>
      </AppMRUProvider>
      {import.meta.env.VITE_STAGE === 'sandbox' && (
        <BottomBanner message="You are in sandbox mode. Do not use real data." />
      )}
    </QueryClientProvider>
  );
}

export default App;
